import React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, SubmitHandler, useWatch, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ButtonProps } from '@node-space/storybook-components/dist/components/Button'
import {
  Box,
  Box as Form,
  Box as GroupFields,
  Box as InlineFields,
} from '@node-space/storybook-components/dist/Box'
import {
  DateErrorType,
  DateOfBirth,
  DateType,
} from '@node-space/storybook-components/dist/DateOfBirth'
import { Icon } from '@node-space/storybook-components/dist/Icon'
import { Input } from '@node-space/storybook-components/dist/Input'
import {
  Modal,
  ModalActions,
  ModalBody,
  ModalNavBar,
} from '@node-space/storybook-components/dist/Modal'
import { Select } from '@node-space/storybook-components/dist/Select'
import { InputLabel } from '@node-space/storybook-components/dist/FormElements'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useDateOfBirthInput } from 'hooks/useDateOfBirthInput'
import {
  BusinessDirectorsFormData,
  BusinessDirectorsFormKeys,
  BusinessDirectorsSchema,
} from '../schemas/businessDirectorsSchema'
import { TEMP_ID_TAG } from './BusinessDirectors'

export type DirectorModalData = BusinessDirectorsFormData & {
  id: number | string
}

type BusinessDirectorModalProps = {
  directorData: DirectorModalData
  isEditingDirector: boolean
  onSaveDirector: (savedDirectorData: DirectorModalData) => void
  onDeleteDirector: (directorId: number | string) => void
  onClose: () => void
}

export const BusinessDirectorModal = ({
  isEditingDirector,
  directorData,
  onSaveDirector,
  onDeleteDirector,
  onClose,
}: BusinessDirectorModalProps) => {
  const { t } = useTranslation()

  const {
    dateOfBirthLabels,
    hasDateOfBirthErrors,
    getDateOfBirthFields,
    setDateOfBirthInputValue,
    setDateOfBirthErrors,
    getDateOfBirthErrors,
    getDateOfBirthErrorText,
    formatDateOfBirthData,
    formatDateOfBirthInput,
  } = useDateOfBirthInput()

  const { data: countryOptions = [], isLoading: isLoadingCountries } = useCountriesQuery(false)

  const defaultValues: BusinessDirectorsFormData = {
    ...directorData,
    dateOfBirth: formatDateOfBirthData(directorData.dateOfBirth),
  }

  const formSchema = BusinessDirectorsSchema(t)
  const form = useForm<BusinessDirectorsFormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setSelectField } = useFormFields<BusinessDirectorsFormKeys>(
    formSchema,
    form,
    formValues
  )

  const formFields: { [key in BusinessDirectorsFormKeys] } = {
    firstName: setInputField({ key: 'firstName', useLabelAsPlaceholder: true, showLabel: false }),
    lastName: setInputField({ key: 'lastName', useLabelAsPlaceholder: true, showLabel: false }),
    dateOfBirth: {}, // handled as custom control
    address1: setInputField({ key: 'address1', useLabelAsPlaceholder: true, showLabel: false }),
    address2: setInputField({ key: 'address2', useLabelAsPlaceholder: true, showLabel: false }),
    city: setInputField({ key: 'city', useLabelAsPlaceholder: true, showLabel: false }),
    postalCode: setInputField({ key: 'postalCode', useLabelAsPlaceholder: true, showLabel: false }),
    countryCode: setSelectField({
      key: 'countryCode',
      options: countryOptions,
      placeholder: t('formFields.chooseCountry'),
      showLabel: false,
    }),
  }

  const deleteButton: ButtonProps = {
    children: t('delete'),
    destructive: true,
    iconElement: <Icon name="TrashIcon" color="white" />,
    onClick: () => onDeleteDirector(directorData.id),
  }

  const addButton: ButtonProps = {
    name: isEditingDirector ? 'save-director' : 'add-director',
    children: isEditingDirector ? `${t('save')}` : `${t('addDirector')}`,
    type: 'submit',
    disabled: !!Object.keys(errors)?.length || hasDateOfBirthErrors,
  }

  const onSubmit: SubmitHandler<typeof formValues> = (formValues: BusinessDirectorsFormData) => {
    const id =
      directorData?.id ?? `${TEMP_ID_TAG}${`${formValues?.firstName} ${formValues?.lastName}`}`

    const updatedDirector = {
      ...formValues,
      id,
      dateOfBirth: formatDateOfBirthInput(formValues.dateOfBirth),
    }

    onSaveDirector(updatedDirector)
  }

  return (
    <Modal visible closeModal={onClose} allowOverFlow>
      <ModalNavBar title={t('addDirector')} onClose={onClose} />
      <Form tag="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalBody spaceChildren={24}>
          <Box>
            <InputLabel>{t('verification.businessDirectors.legalNameOfPerson')}</InputLabel>
            <GroupFields flex direction="col" gapY={8}>
              <Input {...formFields.firstName} />
              <Input {...formFields.lastName} />
            </GroupFields>
          </Box>
          <Box>
            <Controller
              control={control}
              name="dateOfBirth"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <DateOfBirth
                  date={getDateOfBirthFields(value)}
                  labels={{
                    ...dateOfBirthLabels,
                    errorMessage: getDateOfBirthErrorText(error),
                  }}
                  errors={getDateOfBirthErrors(error)}
                  onChange={(date: DateType) => onChange(setDateOfBirthInputValue(date))}
                  onValidate={(errors: DateErrorType) => setDateOfBirthErrors(errors)}
                />
              )}
            />
          </Box>
          <Box>
            <InputLabel>{t('verification.businessDirectors.residentialAddress')}</InputLabel>
            <GroupFields flex direction="col" gapY={8}>
              <Input {...formFields.address1} />
              <Input {...formFields.address2} />
              <InlineFields flex gap={8}>
                <Box flexSize="fill" width="full">
                  <Input {...formFields.city} />
                </Box>
                <Box flexSize="fill" width="full">
                  <Input {...formFields.postalCode} />
                </Box>
              </InlineFields>
              <Select {...formFields.countryCode} loading={isLoadingCountries} isSearchable />
            </GroupFields>
          </Box>
        </ModalBody>
        <ModalActions actions={isEditingDirector ? [deleteButton, addButton] : [addButton]} />
      </Form>
    </Modal>
  )
}
